/**
 * Electron Admin Status
 *
 * Local system health page showing PostgreSQL, Ollama, storage paths,
 * service availability flags, BYOK provider count, and app version.
 * Only rendered on Electron — cloud uses AdminStatus.
 *
 * @module @humanizer/studio/components/admin/ElectronAdminStatus
 */

import { useState, useEffect, useRef } from 'react';
import { useLocalAdmin } from '../../hooks/useLocalAdmin';
import type { SystemStatusResult } from '../../types/electron';

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function ElectronAdminStatus() {
  const { getSystemStatus } = useLocalAdmin();
  const [status, setStatus] = useState<SystemStatusResult | null>(null);
  const [archiveStatus, setArchiveStatus] = useState<{ connected: boolean; stats?: any } | null>(null);
  const [ollamaModels, setOllamaModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    (async () => {
      try {
        const systemStatus = await getSystemStatus();
        setStatus(systemStatus);
      } catch {
        // non-critical — page will show empty state
      }

      // Also fetch archive status for detailed PG info
      if (window.electronArchive) {
        try {
          const aStatus = await window.electronArchive.status();
          setArchiveStatus(aStatus);
        } catch {
          // non-critical
        }
      }

      // Fetch Ollama model list
      if (window.electronOllama) {
        try {
          const models = await window.electronOllama.listModels();
          setOllamaModels(models ?? []);
        } catch {
          // non-critical
        }
      }

      setLoading(false);
    })();
  }, [getSystemStatus]);

  if (loading || !status) {
    return (
      <div className="admin-loading">
        <span className="admin-loading__spinner" />
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* App Info */}
      <div className="admin-section">
        <div className="admin-section__header">
          <h2 className="admin-section__title">Application</h2>
        </div>
        <div className="admin-section__content">
          <table className="admin-table">
            <tbody>
              <tr>
                <td>App Version</td>
                <td>{status.appVersion}</td>
              </tr>
              <tr>
                <td>Platform</td>
                <td>{status.platform}</td>
              </tr>
              <tr>
                <td>Active Profile</td>
                <td>{status.profile ? `${status.profile.displayName} (${status.profile.localRole})` : 'None'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Service Status */}
      <div className="admin-section">
        <div className="admin-section__header">
          <h2 className="admin-section__title">Services</h2>
        </div>
        <div className="admin-section__content">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Status</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>PostgreSQL</td>
                <td>
                  <span className={`admin-badge admin-badge--${status.services.postgres ? 'success' : 'error'}`}>
                    {status.services.postgres ? 'Connected' : 'Disconnected'}
                  </span>
                </td>
                <td>
                  {archiveStatus?.connected && archiveStatus.stats
                    ? `${archiveStatus.stats.totalNodes?.toLocaleString() ?? 0} nodes`
                    : status.services.postgres ? 'Connected' : 'Not initialized'}
                </td>
              </tr>
              <tr>
                <td>Ollama</td>
                <td>
                  <span className={`admin-badge admin-badge--${status.services.ollama ? 'success' : 'warning'}`}>
                    {status.services.ollama ? 'Online' : 'Offline'}
                  </span>
                </td>
                <td>
                  {status.services.ollama
                    ? `${status.ollamaModelCount} model${status.ollamaModelCount !== 1 ? 's' : ''}`
                    : 'Not running'}
                </td>
              </tr>
              <tr>
                <td>Usage Tracking</td>
                <td>
                  <span className={`admin-badge admin-badge--${status.services.usageTracking ? 'success' : 'neutral'}`}>
                    {status.services.usageTracking ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{status.services.usageTracking ? 'Recording LLM calls' : 'Requires PostgreSQL'}</td>
              </tr>
              <tr>
                <td>Prompt Service</td>
                <td>
                  <span className={`admin-badge admin-badge--${status.services.promptService ? 'success' : 'neutral'}`}>
                    {status.services.promptService ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{status.services.promptService ? 'Overrides available' : 'Requires PostgreSQL'}</td>
              </tr>
              <tr>
                <td>Quota Cache</td>
                <td>
                  <span className={`admin-badge admin-badge--${status.services.quotaCache ? 'success' : 'neutral'}`}>
                    {status.services.quotaCache ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{status.services.quotaCache ? 'Cloud quota cached locally' : 'Requires PostgreSQL'}</td>
              </tr>
              <tr>
                <td>BYOK Providers</td>
                <td>
                  <span className={`admin-badge admin-badge--${status.byokProviderCount > 0 ? 'info' : 'neutral'}`}>
                    {status.byokProviderCount} configured
                  </span>
                </td>
                <td>{status.byokProviderCount > 0 ? 'API keys stored in keychain' : 'None configured'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Ollama Models */}
      {ollamaModels.length > 0 && (
        <div className="admin-section">
          <div className="admin-section__header">
            <h2 className="admin-section__title">Ollama Models</h2>
          </div>
          <div className="admin-section__content">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Model</th>
                </tr>
              </thead>
              <tbody>
                {ollamaModels.map((model) => (
                  <tr key={model}>
                    <td>{model}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Storage Paths */}
      <div className="admin-section">
        <div className="admin-section__header">
          <h2 className="admin-section__title">Storage Paths</h2>
        </div>
        <div className="admin-section__content">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Path</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Media Root</td>
                <td><code>{status.storagePaths.mediaRoot}</code></td>
              </tr>
              <tr>
                <td>Backup Directory</td>
                <td><code>{status.storagePaths.backupDir}</code></td>
              </tr>
              <tr>
                <td>User Data</td>
                <td><code>{status.storagePaths.userData}</code></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
